"use client";

import { useState, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Card } from "@/components/ui/Card";
import PublicLayout from "@/components/layout/PublicLayout";
import Link from "next/link";
import { ArrowRight, ArrowLeft, Building2, User, Lock, MapPin, Phone, Mail, Eye, EyeOff } from "lucide-react";
import type { ServiceMode } from "@/lib/service-mode";

export const dynamic = "force-dynamic";

declare global {
  interface Window {
    grecaptcha: any;
  }
}

export default function SignupContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const referralCode = searchParams.get("ref");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [step, setStep] = useState(1);
  const [verificationStatus, setVerificationStatus] = useState<'idle' | 'verifying' | 'success' | 'error'>('idle');
  const [form, setForm] = useState({
    // Step 1: Account
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    // Step 2: Garage Details
    garageName: "",
    phone: "",
    serviceMode: "GARAGE_ONLY" as ServiceMode,
    address: "",
    postcode: "",
    city: "",
    county: "",
    // Step 3: Optional
    shortDescription: "",
    description: "",
    website: "",
  });

  useEffect(() => {
    // Capture referral code from URL first
    if (referralCode) {
      console.log("Found referral code in URL:", referralCode);
      // Store in both localStorage (persistence) and sessionStorage (session tracking)
      localStorage.setItem("referral_code", referralCode);
      localStorage.setItem("referral_code_timestamp", Date.now().toString());
      if (typeof window !== "undefined" && window.sessionStorage) {
        const trackingParams = { referralCode };
        sessionStorage.setItem("tracking_params", JSON.stringify(trackingParams));
      }
    }

    // Also initialize referral tracking (captures UTM params, etc)
    import("@/lib/referral-tracking").then(({ initializeReferralTracking }) => {
      initializeReferralTracking();
    });

    // Load reCAPTCHA script only if configured
    const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
    if (siteKey && !siteKey.includes("YOUR_RECAPTCHA")) {
      const script = document.createElement("script");
      script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
      script.async = true;
      script.defer = true;
      script.onload = () => setRecaptchaReady(true);
      document.head.appendChild(script);
    } else {
      // Skip reCAPTCHA in development
      setRecaptchaReady(true);
    }
  }, [referralCode]);

  const validateStep = () => {
    setError(null);
    if (step === 1) {
      if (!form.name.trim() || !form.email.trim() || !form.password.trim() || !form.confirmPassword.trim()) {
        setError("Please complete all fields");
        return false;
      }
      if (form.password.length < 8) {
        setError("Password must be at least 8 characters");
        return false;
      }
      if (form.password !== form.confirmPassword) {
        setError("Passwords do not match");
        return false;
      }
    } else if (step === 2) {
      if (!form.garageName.trim() || !form.phone.trim()) {
        setError("Garage name and phone are required");
        return false;
      }

      if (form.serviceMode === "MOBILE_ONLY") {
        if (!form.postcode.trim() && !form.city.trim() && !form.county.trim()) {
          setError("For mobile-only service, please provide at least one service area field (postcode, city, or county)");
          return false;
        }
      } else {
        if (!form.address.trim() || !form.postcode.trim() || !form.city.trim() || !form.county.trim()) {
          setError("Please complete all required workshop address fields");
          return false;
        }
      }

      if (!["GARAGE_ONLY", "MOBILE_ONLY", "HYBRID"].includes(form.serviceMode)) {
        setError("Please select a valid service mode");
        return false;
      }
    }
    return true;
  };

  const handleGeocodePostcode = async (postcode: string) => {
    if (!postcode || postcode.length < 5) {
      setVerificationStatus('idle');
      return;
    }
    setVerificationStatus('verifying');
    try {
      const res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(postcode)}`);
      if (res.ok) {
        const data = await res.json();
        if (data.result) {
          setVerificationStatus('success');
          if (data.result.admin_district) {
            setForm(prev => ({ ...prev, city: data.result.admin_district }));
          }
          if (data.result.admin_county) {
            setForm(prev => ({ ...prev, county: data.result.admin_county }));
          }
        }
      } else {
        setVerificationStatus('error');
      }
    } catch {
      setVerificationStatus('error');
    }
  };

  useEffect(() => {
    if (step === 2 && form.postcode.length >= 5) {
      const timeoutId = setTimeout(() => handleGeocodePostcode(form.postcode), 800);
      return () => clearTimeout(timeoutId);
    }
  }, [form.postcode, step]);

  const handleNext = () => {
    if (validateStep()) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    setError(null);
    setStep(step - 1);
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep()) return;

    setLoading(true);
    setError(null);

    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
      localStorage.removeItem("garage-data");
      localStorage.removeItem("user");

      // Get reCAPTCHA token (skip if not configured)
      let recaptchaToken = "";
      const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
      
      if (siteKey && !siteKey.includes("YOUR_RECAPTCHA") && window.grecaptcha) {
        recaptchaToken = await window.grecaptcha.execute(siteKey, { action: "signup" });
      }

      const response = await fetch("/api/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          password: form.password,
          recaptchaToken,
          garageName: form.garageName,
          phone: form.phone,
          serviceMode: form.serviceMode,
          address: form.address,
          postcode: form.postcode,
          city: form.city,
          county: form.county,
          shortDescription: form.shortDescription || null,
          description: form.description || null,
          website: form.website || null,
        }),
      });

      if (!response.ok) {
        try {
          const data = await response.json();
          setError(data.error || "Signup failed");
        } catch {
          setError(`Signup failed with status ${response.status}`);
        }
        return;
      }

      const data = await response.json();

      // Store garage data from signup
      if (data.garage) {
        localStorage.setItem("garage-data", JSON.stringify(data.garage));
      }

      // Auto-login after successful signup
      const loginResponse = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: form.email,
          password: form.password,
          recaptchaToken: recaptchaToken || "",
        }),
      });

      if (loginResponse.ok) {
        const userData = await loginResponse.json();
        localStorage.setItem("user", JSON.stringify(userData.user));
        
        // Store full garage data from login response if available
        if (userData.garage) {
          localStorage.setItem("garage-data", JSON.stringify(userData.garage));
        } else if (userData.user.garages && userData.user.garages[0]) {
          localStorage.setItem("garage-data", JSON.stringify(userData.user.garages[0]));
        }
        // record referral conversion if applicable
        try {
          const { recordReferralConversion } = await import("@/lib/referral-tracking");
          await recordReferralConversion(form.email, form.name);
        } catch (err) {
          console.error("failed to record referral conversion", err);
        }
        
        // Always redirect to garage dashboard after signup
        router.push("/garage/dashboard");
      } else {
        setError("Account created! Please login.");
        setTimeout(() => {
          router.push("/login");
        }, 2000);
      }
    } catch (error) {
      console.error("Signup error:", error);
      setError("An error occurred during signup");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicLayout>
      <div className="bg-linear-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-6 min-h-[70vh]">
        <div className="w-full max-w-2xl space-y-6">
          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-2">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  s === step ? "bg-blue-600 text-white" : s < step ? "bg-green-500 text-white" : "bg-slate-300 text-slate-600"
                }`}>
                  {s}
                </div>
                {s < 3 && <div className={`w-12 h-1 ${
                  s < step ? "bg-green-500" : "bg-slate-300"
                }`} />}
              </div>
            ))}
          </div>

          <Card className="p-8">
            {step === 1 && (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <User className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">Create Account</h2>
                    <p className="text-sm text-slate-600">Step 1: Your account details</p>
                  </div>
                </div>

                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {error}
                  </div>
                )}

                <form onSubmit={(e) => { e.preventDefault(); handleNext(); }} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Full Name *</label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="John Doe"
                      autoComplete="name"
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Email *</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="your@email.com"
                      autoComplete="email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Password *</label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                        className="w-full px-4 py-3 pr-12 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="At least 8 characters"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        className="absolute inset-y-0 right-0 px-3 text-slate-500 hover:text-slate-700"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Confirm Password *</label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={form.confirmPassword}
                        onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                        className="w-full px-4 py-3 pr-12 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Confirm your password"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        className="absolute inset-y-0 right-0 px-3 text-slate-500 hover:text-slate-700"
                        aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                      >
                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    Next: Garage Details <ArrowRight className="w-5 h-5" />
                  </button>
                </form>
              </>
            )}

            {step === 2 && (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">Garage Details</h2>
                    <p className="text-sm text-slate-600">Step 2: Tell us about your garage</p>
                  </div>
                </div>

                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {error}
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Garage Name *</label>
                    <input
                      type="text"
                      value={form.garageName}
                      onChange={(e) => setForm({ ...form, garageName: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="ABC Motors Ltd"
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Phone *</label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="01234 567890"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Service Mode *</label>
                    <select
                      value={form.serviceMode}
                      onChange={(e) => setForm({ ...form, serviceMode: e.target.value as ServiceMode })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    >
                      <option value="GARAGE_ONLY">Garage only</option>
                      <option value="MOBILE_ONLY">Mobile only</option>
                      <option value="HYBRID">Garage + Mobile</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Street Address {form.serviceMode === "MOBILE_ONLY" ? "(optional for mobile-only)" : "*"}
                    </label>
                    <input
                      type="text"
                      value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="123 High Street"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Postcode {form.serviceMode === "MOBILE_ONLY" ? "(service area)" : "*"}
                      </label>
                      <input
                        type="text"
                        value={form.postcode}
                        onChange={(e) => setForm({ ...form, postcode: e.target.value.toUpperCase() })}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="SW1A 1AA"
                      />
                      {verificationStatus === 'verifying' && <p className="text-xs text-blue-600 mt-1">Verifying...</p>}
                      {verificationStatus === 'success' && <p className="text-xs text-green-600 mt-1">✓ Verified</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        City / Town {form.serviceMode === "MOBILE_ONLY" ? "(service area)" : "*"}
                      </label>
                      <input
                        type="text"
                        value={form.city}
                        onChange={(e) => setForm({ ...form, city: e.target.value })}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="London"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        County {form.serviceMode === "MOBILE_ONLY" ? "(service area)" : "*"}
                      </label>
                      <input
                        type="text"
                        value={form.county}
                        onChange={(e) => setForm({ ...form, county: e.target.value })}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Greater London"
                      />
                    </div>
                  </div>
                  {form.serviceMode === "MOBILE_ONLY" && (
                    <p className="text-xs text-slate-500">
                      Mobile-only garages can hide workshop address and show service coverage area instead.
                    </p>
                  )}
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={handleBack}
                      className="flex-1 py-3 bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <ArrowLeft className="w-5 h-5" /> Back
                    </button>
                    <button
                      type="button"
                      onClick={handleNext}
                      className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      Next: About Your Garage <ArrowRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Lock className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">About Your Garage</h2>
                    <p className="text-sm text-slate-600">Step 3: Optional (can add later)</p>
                  </div>
                </div>

                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSignup} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Short Description (SEO)</label>
                    <input
                      type="text"
                      value={form.shortDescription}
                      onChange={(e) => setForm({ ...form, shortDescription: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Independent garage specializing in MOT and servicing"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Full Description</label>
                    <textarea
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Tell customers about your garage, experience, and services..."
                      rows={4}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Website</label>
                    <input
                      type="text"
                      value={form.website}
                      onChange={(e) => setForm({ ...form, website: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="yourgaragename.com"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={handleBack}
                      className="flex-1 py-3 bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <ArrowLeft className="w-5 h-5" /> Back
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 py-3 bg-green-600 hover:bg-green-700 disabled:bg-slate-400 text-white font-bold rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Creating Account...
                        </>
                      ) : (
                        <>
                          Create Account <ArrowRight className="w-5 h-5" />
                        </>
                      )}
                    </button>
                  </div>
                </form>

                <div className="text-center mt-6">
                  <p className="text-slate-600 text-sm">
                    Already have an account?{" "}
                    <Link href="/login" className="text-blue-600 hover:underline font-medium">
                      Login
                    </Link>
                  </p>
                </div>
              </>
            )}
          </Card>
        </div>
      </div>
    </PublicLayout>
  );
}
